import React, { useState } from 'react';
import { ClipboardCheckIcon, ClockIcon, ThermometerIcon, GaugeIcon, DatabaseIcon, ScaleIcon, BeakerIcon, PackageIcon, TruckIcon, ArrowUpIcon, ArrowDownIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface AutomatedData {
  timestamp: string;
  tanks: {
    ansatzTanks: {
      id: string;
      level: number;
      diff: number;
      calculatedDiff: number;
      maxCapacity: number;
    }[];
    zusatzTanks: {
      id: string;
      level: number;
      diff: number;
      calculatedDiff: number;
      maxCapacity: number;
      chargenNr: string;
    }[];
    lagerTanks: {
      id: number;
      level: number;
      diff: number;
      calculatedDiff: number;
      maxCapacity: number;
      sourceFlow: string;
      sourceAmount: number;
    }[];
  };
  machines: {
    id: string;
    name: string;
    operatingHours: number;
    hoursDiff: number;
    rotor: number;
    pump: number;
    current: number;
    pressure: number;
    temperature: number;
    pearls: {
      amount: number;
      tendency: 'up' | 'down';
      lastChange?: string;
      wearPercentage: number;
    };
  }[];
  shipments: {
    id: string;
    type: string;
    amount: number;
    source: string;
    customer: string;
    containers: number;
    containerWeight: number;
  }[];
}
export function AutomatedDataList() {
  const [expandedEntries, setExpandedEntries] = useState<{
    [key: string]: boolean;
  }>({});
  const toggleExpand = (timestamp: string) => {
    setExpandedEntries(prev => ({
      ...prev,
      [timestamp]: !prev[timestamp]
    }));
  };
  // Updated sample data with two additional entries
  const automatedData: AutomatedData[] = [{
    timestamp: '15:30',
    tanks: {
      ansatzTanks: [{
        id: 'AT1',
        level: 15.8,
        diff: 0.6,
        calculatedDiff: 0.5,
        maxCapacity: 25
      }, {
        id: 'AT2',
        level: 12.9,
        diff: 0.5,
        calculatedDiff: 0.4,
        maxCapacity: 25
      }],
      zusatzTanks: [{
        id: 'Entschäumer',
        level: 10.5,
        diff: 0.3,
        calculatedDiff: 0.2,
        maxCapacity: 15,
        chargenNr: 'CH-2025-042'
      }, {
        id: 'Dispergier',
        level: 16.0,
        diff: 0.5,
        calculatedDiff: 0.4,
        maxCapacity: 20,
        chargenNr: 'CH-2025-043'
      }, {
        id: 'IBC',
        level: 0,
        diff: 0,
        calculatedDiff: 0,
        maxCapacity: 1000,
        chargenNr: 'CH-2025-044'
      }],
      lagerTanks: [{
        id: 1,
        level: 33.5,
        diff: 0.5,
        calculatedDiff: 27.7,
        maxCapacity: 50,
        sourceFlow: 'AT1',
        sourceAmount: 27.7
      }, {
        id: 2,
        level: 42.4,
        diff: 2.8,
        calculatedDiff: 2.8,
        maxCapacity: 55,
        sourceFlow: 'AT1',
        sourceAmount: 2.8
      }, {
        id: 3,
        level: 43.1,
        diff: 0,
        calculatedDiff: 0,
        maxCapacity: 60,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 4,
        level: 3.9,
        diff: -29.1,
        calculatedDiff: 0,
        maxCapacity: 45,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 5,
        level: 12.5,
        diff: -30.6,
        calculatedDiff: 13.4,
        maxCapacity: 65,
        sourceFlow: 'AT1',
        sourceAmount: 13.4
      }, {
        id: 6,
        level: 30.5,
        diff: -11.2,
        calculatedDiff: 12.2,
        maxCapacity: 70,
        sourceFlow: 'AT1',
        sourceAmount: 12.2
      }, {
        id: 7,
        level: 10.3,
        diff: -0.2,
        calculatedDiff: 0,
        maxCapacity: 40,
        sourceFlow: '',
        sourceAmount: 0
      }]
    },
    machines: [{
      id: '1',
      name: 'Perlmühle 1 (KD190/Ba)',
      operatingHours: 9242,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 103,
      current: 123,
      pressure: 0.2,
      temperature: 53.2,
      pearls: {
        amount: 630,
        tendency: 'down',
        lastChange: '21.04.2025',
        wearPercentage: 2.3
      }
    }, {
      id: '2',
      name: 'Perlmühle 2 (KD190/Ba2)',
      operatingHours: 2837,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 103,
      current: 127,
      pressure: 0.2,
      temperature: 55.1,
      pearls: {
        amount: 575,
        tendency: 'down',
        wearPercentage: 1.8
      }
    }, {
      id: '3',
      name: 'Perlmühle 3 (KD190/Ba3)',
      operatingHours: 9634,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 101,
      current: 124,
      pressure: 0.1,
      temperature: 56.0,
      pearls: {
        amount: 575,
        tendency: 'down',
        wearPercentage: 1.5
      }
    }, {
      id: '4',
      name: 'Perlmühle 4 (Bühler T4)',
      operatingHours: 259,
      hoursDiff: 1,
      rotor: 9.5,
      pump: 249,
      current: 98.8,
      pressure: 0.33,
      temperature: 58.2,
      pearls: {
        amount: 1150,
        tendency: 'down',
        wearPercentage: 1.2
      }
    }],
    shipments: [{
      id: '1',
      type: 'Tankzug',
      amount: 27.22,
      source: 'Tank 1',
      customer: 'MM',
      containers: 18,
      containerWeight: 23400
    }]
  }, {
    timestamp: '15:00',
    tanks: {
      ansatzTanks: [{
        id: 'AT1',
        level: 15.2,
        diff: -0.6,
        calculatedDiff: -0.5,
        maxCapacity: 25
      }, {
        id: 'AT2',
        level: 12.4,
        diff: -0.5,
        calculatedDiff: -0.4,
        maxCapacity: 25
      }],
      zusatzTanks: [{
        id: 'Entschäumer',
        level: 10.2,
        diff: -0.3,
        calculatedDiff: -0.2,
        maxCapacity: 15,
        chargenNr: 'CH-2025-042'
      }, {
        id: 'Dispergier',
        level: 15.5,
        diff: -0.5,
        calculatedDiff: -0.4,
        maxCapacity: 20,
        chargenNr: 'CH-2025-043'
      }, {
        id: 'IBC',
        level: 0,
        diff: 0,
        calculatedDiff: 0,
        maxCapacity: 1000,
        chargenNr: 'CH-2025-044'
      }],
      lagerTanks: [{
        id: 1,
        level: 33.0,
        diff: -0.5,
        calculatedDiff: 0,
        maxCapacity: 50,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 2,
        level: 39.6,
        diff: -2.8,
        calculatedDiff: 0,
        maxCapacity: 55,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 3,
        level: 43.1,
        diff: 0,
        calculatedDiff: 0,
        maxCapacity: 60,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 4,
        level: 33.0,
        diff: 29.1,
        calculatedDiff: 0,
        maxCapacity: 45,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 5,
        level: 43.1,
        diff: 30.6,
        calculatedDiff: 0,
        maxCapacity: 65,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 6,
        level: 41.7,
        diff: 11.2,
        calculatedDiff: 0,
        maxCapacity: 70,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 7,
        level: 10.5,
        diff: 0.2,
        calculatedDiff: 0,
        maxCapacity: 40,
        sourceFlow: '',
        sourceAmount: 0
      }]
    },
    machines: [{
      id: '1',
      name: 'Perlmühle 1 (KD190/Ba)',
      operatingHours: 9241,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 102,
      current: 122,
      pressure: 0.2,
      temperature: 52.8,
      pearls: {
        amount: 630,
        tendency: 'down',
        lastChange: '21.04.2025',
        wearPercentage: 2.2
      }
    }, {
      id: '2',
      name: 'Perlmühle 2 (KD190/Ba2)',
      operatingHours: 2836,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 102,
      current: 126,
      pressure: 0.2,
      temperature: 54.8,
      pearls: {
        amount: 575,
        tendency: 'down',
        wearPercentage: 1.7
      }
    }, {
      id: '3',
      name: 'Perlmühle 3 (KD190/Ba3)',
      operatingHours: 9633,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 100,
      current: 123,
      pressure: 0.1,
      temperature: 55.7,
      pearls: {
        amount: 575,
        tendency: 'down',
        wearPercentage: 1.4
      }
    }, {
      id: '4',
      name: 'Perlmühle 4 (Bühler T4)',
      operatingHours: 258,
      hoursDiff: 1,
      rotor: 9.5,
      pump: 248,
      current: 98.5,
      pressure: 0.32,
      temperature: 57.9,
      pearls: {
        amount: 1150,
        tendency: 'down',
        wearPercentage: 1.1
      }
    }],
    shipments: []
  }, {
    timestamp: '14:30',
    tanks: {
      ansatzTanks: [{
        id: 'AT1',
        level: 14.6,
        diff: -0.4,
        calculatedDiff: -0.3,
        maxCapacity: 25
      }, {
        id: 'AT2',
        level: 11.9,
        diff: -0.3,
        calculatedDiff: -0.2,
        maxCapacity: 25
      }],
      zusatzTanks: [{
        id: 'Entschäumer',
        level: 9.9,
        diff: -0.2,
        calculatedDiff: -0.1,
        maxCapacity: 15,
        chargenNr: 'CH-2025-042'
      }, {
        id: 'Dispergier',
        level: 15.0,
        diff: -0.3,
        calculatedDiff: -0.2,
        maxCapacity: 20,
        chargenNr: 'CH-2025-043'
      }, {
        id: 'IBC',
        level: 0,
        diff: 0,
        calculatedDiff: 0,
        maxCapacity: 1000,
        chargenNr: 'CH-2025-044'
      }],
      lagerTanks: [{
        id: 1,
        level: 32.5,
        diff: -0.3,
        calculatedDiff: 0,
        maxCapacity: 50,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 2,
        level: 36.8,
        diff: -1.5,
        calculatedDiff: 0,
        maxCapacity: 55,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 3,
        level: 43.1,
        diff: 0,
        calculatedDiff: 0,
        maxCapacity: 60,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 4,
        level: 3.9,
        diff: -29.1,
        calculatedDiff: 0,
        maxCapacity: 45,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 5,
        level: 12.5,
        diff: -30.6,
        calculatedDiff: 0,
        maxCapacity: 65,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 6,
        level: 30.5,
        diff: -11.2,
        calculatedDiff: 0,
        maxCapacity: 70,
        sourceFlow: '',
        sourceAmount: 0
      }, {
        id: 7,
        level: 10.3,
        diff: -0.2,
        calculatedDiff: 0,
        maxCapacity: 40,
        sourceFlow: '',
        sourceAmount: 0
      }]
    },
    machines: [{
      id: '1',
      name: 'Perlmühle 1 (KD190/Ba)',
      operatingHours: 9240,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 101,
      current: 121,
      pressure: 0.2,
      temperature: 52.5,
      pearls: {
        amount: 630,
        tendency: 'down',
        lastChange: '21.04.2025',
        wearPercentage: 2.1
      }
    }, {
      id: '2',
      name: 'Perlmühle 2 (KD190/Ba2)',
      operatingHours: 2835,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 101,
      current: 125,
      pressure: 0.2,
      temperature: 54.5,
      pearls: {
        amount: 575,
        tendency: 'down',
        wearPercentage: 1.6
      }
    }, {
      id: '3',
      name: 'Perlmühle 3 (KD190/Ba3)',
      operatingHours: 9632,
      hoursDiff: 1,
      rotor: 12.0,
      pump: 99,
      current: 122,
      pressure: 0.1,
      temperature: 55.4,
      pearls: {
        amount: 575,
        tendency: 'down',
        wearPercentage: 1.3
      }
    }, {
      id: '4',
      name: 'Perlmühle 4 (Bühler T4)',
      operatingHours: 257,
      hoursDiff: 1,
      rotor: 9.5,
      pump: 247,
      current: 98.2,
      pressure: 0.31,
      temperature: 57.6,
      pearls: {
        amount: 1150,
        tendency: 'down',
        wearPercentage: 1.0
      }
    }],
    shipments: []
  }];
  return <div className="space-y-6" data-id="element-159">
      <div className="flex items-center justify-between" data-id="element-160">
        <div className="flex items-center gap-2" data-id="element-161">
          <ClipboardCheckIcon className="text-[#3498dd]" size={20} data-id="element-162" />
          <h3 className="text-lg font-semibold text-gray-800" data-id="element-163">
            Automatische Datenerfassung
          </h3>
        </div>
        <div className="text-sm text-gray-500" data-id="element-164">
          Erfassungsintervall: 30 Minuten
        </div>
      </div>
      {automatedData.map((data, index) => <div key={index} className="bg-white rounded-lg border border-gray-200" data-id="element-165">
          <button onClick={() => toggleExpand(data.timestamp)} className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors" data-id="element-166">
            <div className="flex items-center gap-2" data-id="element-167">
              <ClockIcon size={14} className="text-gray-500" data-id="element-168" />
              <span className="text-sm font-medium text-gray-700" data-id="element-169">
                {data.timestamp}
              </span>
            </div>
            {expandedEntries[data.timestamp] ? <ChevronUpIcon size={16} className="text-gray-500" data-id="element-170" /> : <ChevronDownIcon size={16} className="text-gray-500" data-id="element-171" />}
          </button>
          {expandedEntries[data.timestamp] && <div className="p-4 space-y-8" data-id="element-172">
              {/* Tank Status Section */}
              <div data-id="element-173">
                <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2" data-id="element-174">
                  <DatabaseIcon size={14} className="text-[#3498dd]" data-id="element-175" />
                  Tankbestände
                </h4>
                {/* Ansatz Tanks */}
                <div className="mb-6" data-id="element-176">
                  <h5 className="text-xs font-medium text-gray-600 mb-2" data-id="element-177">
                    Ansatztanks
                  </h5>
                  <div className="overflow-x-auto" data-id="element-178">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-179">
                      <thead className="bg-gray-50" data-id="element-180">
                        <tr className="text-xs text-gray-500" data-id="element-181">
                          <th className="px-3 py-2 text-left" data-id="element-182">Tank</th>
                          <th className="px-3 py-2 text-right" data-id="element-183">Tankinhalt</th>
                          <th className="px-3 py-2 text-right" data-id="element-184">Ist-Diff.</th>
                          <th className="px-3 py-2 text-right" data-id="element-185">
                            Errech. Diff.
                          </th>
                          <th className="px-3 py-2 text-right" data-id="element-186">
                            Max. Kapazität
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white" data-id="element-187">
                        {data.tanks.ansatzTanks.map(tank => <tr key={tank.id} className="text-xs" data-id="element-188">
                            <td className="px-3 py-2 font-medium" data-id="element-189">{tank.id}</td>
                            <td className="px-3 py-2 text-right" data-id="element-190">
                              {tank.level}t
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-191">
                              <span className={tank.diff >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-192">
                                {tank.diff >= 0 ? '+' : ''}
                                {tank.diff}t
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-193">
                              <span className={tank.calculatedDiff >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-194">
                                {tank.calculatedDiff >= 0 ? '+' : ''}
                                {tank.calculatedDiff}t
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-195">
                              {tank.maxCapacity}t
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Zusatz Tanks */}
                <div className="mb-6" data-id="element-196">
                  <h5 className="text-xs font-medium text-gray-600 mb-2" data-id="element-197">
                    Zusatzstoffe
                  </h5>
                  <div className="overflow-x-auto" data-id="element-198">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-199">
                      <thead className="bg-gray-50" data-id="element-200">
                        <tr className="text-xs text-gray-500" data-id="element-201">
                          <th className="px-3 py-2 text-left" data-id="element-202">Tank</th>
                          <th className="px-3 py-2 text-left" data-id="element-203">Chargen-Nr.</th>
                          <th className="px-3 py-2 text-right" data-id="element-204">Tankinhalt</th>
                          <th className="px-3 py-2 text-right" data-id="element-205">Ist-Diff.</th>
                          <th className="px-3 py-2 text-right" data-id="element-206">
                            Errech. Diff.
                          </th>
                          <th className="px-3 py-2 text-right" data-id="element-207">
                            Max. Kapazität
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white" data-id="element-208">
                        {data.tanks.zusatzTanks.map(tank => <tr key={tank.id} className="text-xs" data-id="element-209">
                            <td className="px-3 py-2 font-medium" data-id="element-210">{tank.id}</td>
                            <td className="px-3 py-2" data-id="element-211">
                              <span className="bg-blue-50 text-[#3498dd] px-2 py-0.5 rounded text-xs" data-id="element-212">
                                {tank.chargenNr}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-213">
                              {tank.level}t
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-214">
                              <span className={tank.diff >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-215">
                                {tank.diff >= 0 ? '+' : ''}
                                {tank.diff}t
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-216">
                              <span className={tank.calculatedDiff >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-217">
                                {tank.calculatedDiff >= 0 ? '+' : ''}
                                {tank.calculatedDiff}t
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-218">
                              {tank.maxCapacity}t
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Lager Tanks */}
                <div data-id="element-219">
                  <h5 className="text-xs font-medium text-gray-600 mb-2" data-id="element-220">
                    Lagertanks
                  </h5>
                  <div className="overflow-x-auto" data-id="element-221">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-222">
                      <thead className="bg-gray-50" data-id="element-223">
                        <tr className="text-xs text-gray-500" data-id="element-224">
                          <th className="px-3 py-2 text-left" data-id="element-225">Tank</th>
                          <th className="px-3 py-2 text-right" data-id="element-226">Tankinhalt</th>
                          <th className="px-3 py-2 text-right" data-id="element-227">Ist-Diff.</th>
                          <th className="px-3 py-2 text-right" data-id="element-228">
                            Errech. Diff.
                          </th>
                          <th className="px-3 py-2 text-right" data-id="element-229">
                            Max. Kapazität
                          </th>
                          <th className="px-3 py-2 text-left" data-id="element-230">Zufluss</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white" data-id="element-231">
                        {data.tanks.lagerTanks.map(tank => <tr key={tank.id} className="text-xs" data-id="element-232">
                            <td className="px-3 py-2 font-medium" data-id="element-233">
                              Tank {tank.id}
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-234">
                              {tank.level}t
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-235">
                              <span className={tank.diff >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-236">
                                {tank.diff >= 0 ? '+' : ''}
                                {tank.diff}t
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-237">
                              <span className={tank.calculatedDiff >= 0 ? 'text-green-600' : 'text-red-600'} data-id="element-238">
                                {tank.calculatedDiff >= 0 ? '+' : ''}
                                {tank.calculatedDiff}t
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right" data-id="element-239">
                              {tank.maxCapacity}t
                            </td>
                            <td className="px-3 py-2" data-id="element-240">
                              {tank.sourceFlow && <span className="text-blue-600" data-id="element-241">
                                  {tank.sourceFlow} ({tank.sourceAmount}t)
                                </span>}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {/* Machine Parameters Section */}
              <div data-id="element-242">
                <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2" data-id="element-243">
                  <GaugeIcon size={14} className="text-[#3498dd]" data-id="element-244" />
                  Maschinenparameter
                </h4>
                <div className="overflow-x-auto" data-id="element-245">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-246">
                    <thead className="bg-gray-50" data-id="element-247">
                      <tr className="text-xs text-gray-500" data-id="element-248">
                        <th className="px-3 py-2 text-left" data-id="element-249">Maschine</th>
                        <th className="px-3 py-2 text-right" data-id="element-250">
                          Betriebsstunden
                        </th>
                        <th className="px-3 py-2 text-right" data-id="element-251">Rotor</th>
                        <th className="px-3 py-2 text-right" data-id="element-252">Pumpe</th>
                        <th className="px-3 py-2 text-right" data-id="element-253">Strom</th>
                        <th className="px-3 py-2 text-right" data-id="element-254">Druck</th>
                        <th className="px-3 py-2 text-right" data-id="element-255">Temp.</th>
                        <th className="px-3 py-2 text-right" data-id="element-256">Perlen</th>
                        <th className="px-3 py-2 text-left" data-id="element-257">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white" data-id="element-258">
                      {data.machines.map(machine => <tr key={machine.id} className="text-xs" data-id="element-259">
                          <td className="px-3 py-2 font-medium" data-id="element-260">
                            {machine.name}
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-261">
                            <div className="flex items-center justify-end gap-1" data-id="element-262">
                              {machine.operatingHours}h
                              <span className="text-green-600 bg-green-50 px-1 py-0.5 rounded text-xs" data-id="element-263">
                                +{machine.hoursDiff}h
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-264">
                            {machine.rotor} U/min
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-265">
                            {machine.pump} U/min
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-266">
                            {machine.current} A
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-267">
                            {machine.pressure} bar
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-268">
                            <span className={machine.temperature < 60 ? 'text-green-600' : 'text-red-600'} data-id="element-269">
                              {machine.temperature}°C
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-270">
                            {machine.pearls.amount} kg
                          </td>
                          <td className="px-3 py-2" data-id="element-271">
                            <div className="flex items-center gap-1" data-id="element-272">
                              {machine.pearls.tendency === 'up' ? <span className="text-green-600 bg-green-50 px-1 py-0.5 rounded text-xs flex items-center" data-id="element-273">
                                  <ArrowUpIcon size={12} data-id="element-274" />
                                  <span className="ml-0.5" data-id="element-275">+2%</span>
                                </span> : <span className="text-red-600 bg-red-50 px-1 py-0.5 rounded text-xs flex items-center" data-id="element-276">
                                  <ArrowDownIcon size={12} data-id="element-277" />
                                  <span className="ml-0.5" data-id="element-278">
                                    -{machine.pearls.wearPercentage}%
                                  </span>
                                </span>}
                              {machine.pearls.lastChange && <span className="text-gray-500 ml-2" data-id="element-279">
                                  Letzter Tausch: {machine.pearls.lastChange}
                                </span>}
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Shipping Section */}
              <div data-id="element-280">
                <h4 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2" data-id="element-281">
                  <TruckIcon size={14} className="text-[#3498dd]" data-id="element-282" />
                  Verladung
                </h4>
                <div className="overflow-x-auto" data-id="element-283">
                  <table className="min-w-full divide-y divide-gray-200" data-id="element-284">
                    <thead className="bg-gray-50" data-id="element-285">
                      <tr className="text-xs text-gray-500" data-id="element-286">
                        <th className="px-3 py-2 text-left" data-id="element-287">Typ</th>
                        <th className="px-3 py-2 text-left" data-id="element-288">Quelle</th>
                        <th className="px-3 py-2 text-left" data-id="element-289">Kunde</th>
                        <th className="px-3 py-2 text-right" data-id="element-290">Menge</th>
                        <th className="px-3 py-2 text-right" data-id="element-291">Container</th>
                        <th className="px-3 py-2 text-right" data-id="element-292">Gewicht</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white" data-id="element-293">
                      {data.shipments.map(shipment => <tr key={shipment.id} className="text-xs" data-id="element-294">
                          <td className="px-3 py-2 font-medium" data-id="element-295">
                            {shipment.type}
                          </td>
                          <td className="px-3 py-2" data-id="element-296">{shipment.source}</td>
                          <td className="px-3 py-2" data-id="element-297">
                            <span className="bg-blue-50 text-[#3498dd] px-2 py-0.5 rounded" data-id="element-298">
                              {shipment.customer}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-299">
                            {shipment.amount}t
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-300">
                            {shipment.containers} Stk.
                          </td>
                          <td className="px-3 py-2 text-right" data-id="element-301">
                            {shipment.containerWeight}kg
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>}
        </div>)}
    </div>;
}